import {
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  output,
  Optional,
  Self,
  ViewContainerRef,
  ViewChild,
  ElementRef,
  TemplateRef,
  signal, OnInit,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import {
  MatFormFieldControl,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { BaseMatFormFieldControl } from '@shared/util/base-mat-form-field-control';
import { AddCompanyService } from '../../../../add-entity/services/add-company.service';
import { BehaviorSubject, catchError, debounceTime, finalize, of, switchMap, tap } from 'rxjs';
import { CompanyPostModel } from '../../../../add-entity/models/add-company.model';
import { highlightMatch } from '../add-new-company-dialog.utils';
import { AddressEditModel } from '@shared/models/address-edit.model';
import { toObservable } from '@angular/core/rxjs-interop';
import { capitalizeWords } from '@shared/util/string-helpers';
import { TranslocoPipe, provideTranslocoScope } from '@jsverse/transloco';

@Component({
  selector: 'add-new-company-autocomplete',
  imports: [
    MatFormFieldModule,
    MatAutocompleteModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    AsyncPipe,
    TranslocoPipe,
  ],
  templateUrl: './add-new-company-autocomplete.component.html',
  styleUrl: './add-new-company-autocomplete.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideTranslocoScope('sharedDialogs'),
    {
      provide: MatFormFieldControl,
      useExisting: AddNewCompanyAutocompleteComponent,
    },
  ],
})
export class AddNewCompanyAutocompleteComponent extends BaseMatFormFieldControl<string> implements OnInit {
  @ViewChild('inputElement', { static: true })
  public inputElement!: ElementRef<HTMLInputElement>;
  @ViewChild('autocompleteTemplate', { static: true })
  public autocompleteTemplate!: TemplateRef<any>;

  public inputText = '';
  public inputValue = signal('');
  public inputValue$ = toObservable(this.inputValue);
  public companiesResultsSubject = new BehaviorSubject<CompanyPostModel[]>([]);
  public companies$ = this.companiesResultsSubject.asObservable();
  public result = signal(false);
  public isSearchInFlight = signal(false);
  public searchInProgress = output<boolean>();
  public navigate = output<number>();
  /** MDM-only row selected — parent creates Ideal company then navigates. */
  public mdmCompanySelected = output<CompanyPostModel>();
  public manualEntryStarted = output<CompanyPostModel['name']>();

  public highlightMatch = highlightMatch;
  private overlayRef: OverlayRef | null = null;
  private blurCloseTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private viewContainerRef: ViewContainerRef,
    private addCompanyService: AddCompanyService,
  ) {
    super(ngControl);
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  public focus(): void {
    if (this.inputElement) {
      this.inputElement.nativeElement.focus();
    }
  }

  public get empty(): boolean {
    return !this.inputValue();
  }

  public ngOnInit() {
    this.inputValue$.pipe(
      tap(() => this.result.set(false)),
      debounceTime(300),
      switchMap((value: string) => {
        if (value.length < 3) {
          this.isSearchInFlight.set(false);
          this.searchInProgress.emit(false);
          return of([]);
        }

        this.result.set(true);
        this.isSearchInFlight.set(true);
        return this.addCompanyService.searchCompany(value).pipe(
          catchError(() => { return of([]); }),
          finalize(() => {
            this.isSearchInFlight.set(false);
            this.searchInProgress.emit(false);
          })
        )
      })
    )
      .subscribe((companies: CompanyPostModel[]) => {
        this.companiesResultsSubject.next(companies);
      })
  }

  public onInputTextChange(target: EventTarget | null) {
    if (target instanceof HTMLInputElement) {
      let inputText = target.value;

      inputText = capitalizeWords(inputText);

      if (target.value !== inputText) {
        target.value = inputText;
      }

      this.inputValue.set(inputText);
      this.searchInProgress.emit(inputText.length > 2);

      if (inputText.length >= 3) {
        this.openOverlay();
      } else {
        this.companiesResultsSubject.next([]);
        this.closeOverlay();
      }
    }
  }

  public onInputFocus(): void {
    this.focused = true;

    if (this.inputValue().length >= 3) {
      this.openOverlay();
    }
  }

  public onInputBlur(): void {
    this.focused = false;
    this.blurCloseTimeout = setTimeout(() => {
      this.closeOverlay();
      this.blurCloseTimeout = null;
    }, 200);
  }

  public onManualEntryMouseDown(event: MouseEvent): void {
    event.preventDefault();
  }

  public startManualEntry(): void {
    if (this.blurCloseTimeout) {
      clearTimeout(this.blurCloseTimeout);
      this.blurCloseTimeout = null;
    }

    this.manualEntryStarted.emit(this.inputValue());
    this.closeOverlay();
  }

  public navigateToCompany(id: number): void {
    this.navigate.emit(id);
  }

  public onCompanySelected(company: CompanyPostModel): void {
    if (!company) {
      return;
    }

    if (company.isMdmMatch || !company.id) {
      this.mdmCompanySelected.emit(company);
      this.closeOverlay();
      return;
    }

    this.navigateToCompany(company.id);
  }

  public trackCompany(company: CompanyPostModel): string {
    return company.companyKey
      || company.mdmUri
      || `${company.id ?? 0}-${company.name ?? ''}-${company.offices?.[0]?.address?.streetAddress ?? ''}`;
  }

  public onContainerClick(event: MouseEvent): void {
    this.focus();
  }

  private openOverlay(): void {
    if (this.overlayRef) {
      return;
    }

    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.inputElement)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 14,
          offsetX: -10,
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: -4,
          offsetX: 10,
        },
      ])
      .withPush(false)
      .withViewportMargin(8);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
      width: 740,
      minWidth: 740,
    });

    const templatePortal = new TemplatePortal(
      this.autocompleteTemplate,
      this.viewContainerRef
    );

    this.overlayRef.attach(templatePortal);
  }

  public closeOverlay(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  public formatWebsite(website: string | null): string {
    if (!website) {
      return '--';
    }

    return website.replace('www.', '');
  }

  public checkForHttp(website: string | null): string {
    if (!website) {
      return '';
    }

    if (!/^https?:\/\//i.test(website)) {
      return `https://${website}`;
    }

    return website;
  }

  public hasConflictingCompanyName(companies: CompanyPostModel[] | null | undefined): boolean {
    const typed = this.normalizeCompanyName(this.inputValue());
    if (!typed || !companies?.length) {
      return false;
    }
    return companies.some((company) => this.normalizeCompanyName(company?.name) === typed);
  }

  private normalizeCompanyName(value: string | null | undefined): string {
    return value?.trim().toLowerCase().replace(/\s+/g, ' ') ?? '';
  }

  public formattedAddress(address: AddressEditModel | null | undefined): string {
    if (!address) {
      return ''
    }
    const {
      streetAddress = '',
      city = '',
      stateProvince = '',
      postalCode = '',
      country = ''
    } = address;

    const breakspace = '<br/>'
      ;

    const parts: string[] = [];

    if (streetAddress) parts.push(streetAddress);
    if (city || stateProvince || postalCode) {
      const cityStateZip = [breakspace, city, stateProvince, postalCode].filter(Boolean).join(' ');
      parts.push(cityStateZip);
    }
    if (country) parts.push(country);

    return parts.join(', ');
  }
}
